// queryClientInstance.js

import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { QueryCache, QueryClient } from '@tanstack/react-query'
import { removeOldestQuery } from '@tanstack/react-query-persist-client'
import { createMMKV } from 'react-native-mmkv'
import { MINS_TO_MS } from 'shared/constants'
import getEnvironment from './environment'

const MAX_QUERY_CACHE_AGE = 24 * 60 * MINS_TO_MS

const envName = getEnvironment().name
const persisterKey = `${envName}/v2/queries_cache`

// Initialize the MMKV storage
const storage = createMMKV()

// Create wrapper to make MMKV compatible with the AsyncStorage interface
// All methods must return Promises to match AsyncStorage's interface
const mmkvStorage = {
  getItem: async (key: string) => {
    const value = storage.getString(key)
    return value === undefined ? null : value
  },
  setItem: async (key: string, value: string) => {
    storage.set(key, value)
    return
  },
  removeItem: async (key: string) => {
    storage.remove(key)
    return
  },
  clear: async () => {
    storage.clearAll()
    return
  },
}

// Create persister using MMKV with AsyncStorage persister interface
const mmkvPersister = createAsyncStoragePersister({
  storage: mmkvStorage,
  retry: removeOldestQuery,
  key: persisterKey,
})

const queryClientInstance = configureQueryClient(persisterKey)

export { MAX_QUERY_CACHE_AGE, mmkvPersister, queryClientInstance }

function configureQueryClient(key: string) {
  const queryCache = new QueryCache()

  const client = new QueryClient({
    queryCache,
    defaultOptions: {
      queries: {
        gcTime: MAX_QUERY_CACHE_AGE,
        staleTime: 0.5 * MINS_TO_MS,
        networkMode: 'offlineFirst',
      },
      mutations: {
        networkMode: 'offlineFirst',
      },
    },
  })

  return client
}
